import type { ComponentStyleConfig } from '@chakra-ui/theme';
import { inputAnatomy as parts } from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers, cssVar, defineStyle } from '@chakra-ui/react';
import { Borders, Colors, Shadows, Space } from '../../types';

const { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(parts.keys);

const $height = cssVar('input-height');
const $fontSize = cssVar('input-font-size');
const $padding = cssVar('input-padding');
const $borderRadius = cssVar('input-border-radius');

// base styles and styles for inputs
const invalidBase = {
  borderColor: Colors.SURFACE_ERROR,
  _hover: {
    borderColor: Colors.SURFACE_ERROR,
  },
};

const invalidStyles = {
  borderColor: Colors.SURFACE_ERROR,
  _hover: {
    borderColor: Colors.SURFACE_ERROR,
  },
};

const invalidFlushedStyles = {
  borderColor: Colors.SURFACE_ERROR,
  boxShadow: 'inset 0 -1px 0 0px var(--udl-colors-surface-error)',
};

const focusFlushedStyles = {
  bg: Colors.SURFACE_PAGE,
  borderColor: Colors.BORDER_FOCUS,
  boxShadow: Shadows.OUTLINE,
  _hover: {
    borderColor: Colors.BORDER_EMPHASIS,
  },
};

const focusStyles = {
  bg: Colors.SURFACE_PAGE,
  borderColor: Colors.BORDER_FOCUS,
  boxShadow: 'none',
  color: Colors.TEXT_EMPHASIS,
  outline: '2px solid',
  outlineColor: Colors.BORDER_FOCUS,
  outlineOffset: '-1px',
};

const readOnlyStyles = {
  boxShadow: 'none !important',
  color: Colors.TEXT_MUTED,
  bg: Colors.SURFACE_PAGE,
  userSelect: 'all',
  cursor: 'not-allowed',
  borderColor: Colors.BORDER_DEFAULT,
  _hover: {
    borderColor: Colors.BORDER_DEFAULT,
  },
};

const baseStyle = {
  addon: {
    height: $height.reference,
    fontSize: $fontSize.reference,
    borderRadius: $borderRadius.reference,
    color: Colors.TEXT_SUBDUED,
  },
  element: {
    color: Colors.TEXT_SUBDUED,
    '&:has(button)': {
      w: 'fit-content',
      ':is(.chakra-input__left-element)': {
        ps: `calc(${$padding.reference} / 3)`,
      },
      ':is(.chakra-input__right-element)': {
        pe: `calc(${$padding.reference} / 3)`,
      },
    },
  },
  field: {
    '::-webkit-search-cancel-button': {
      display: 'none',
    },
    width: '100%',
    height: $height.reference,
    fontSize: $fontSize.reference,
    px: $padding.reference,
    borderRadius: $borderRadius.reference,
    minWidth: 0,
    bg: Colors.SURFACE_PAGE,
    color: Colors.TEXT_EMPHASIS,
    _readOnly: readOnlyStyles,
    _invalid: invalidBase,
    _disabled: {
      opacity: 1,
      cursor: 'not-allowed',
      borderColor: Colors.SURFACE_DISABLED,
      bg: Colors.SURFACE_DISABLED,
      color: Colors.TEXT_DISABLED,
      _hover: {
        borderColor: Colors.SURFACE_DISABLED,
        bg: Colors.SURFACE_DISABLED,
        color: Colors.TEXT_DISABLED,
      },
    },
  },
};

const size = {
  lg: defineStyle({
    [$fontSize.variable]: `fontSizes.lg`,
    [$padding.variable]: `space.4`,
    [$borderRadius.variable]: `radii.xl`,
    [$height.variable]: `sizes.12`,
  }),
  md: defineStyle({
    [$fontSize.variable]: `fontSizes.sm`,
    [$padding.variable]: `space.3`,
    [$borderRadius.variable]: `radii.lg`,
    [$height.variable]: `sizes.10`,
  }),
  sm: defineStyle({
    [$fontSize.variable]: `fontSizes.sm`,
    [$padding.variable]: `space.3`,
    [$borderRadius.variable]: `radii.md`,
    [$height.variable]: `sizes.8`,
  }),
  xs: defineStyle({
    [$fontSize.variable]: `fontSizes.xs`,
    [$padding.variable]: `space.2`,
    [$borderRadius.variable]: `radii.base`,
    [$height.variable]: `sizes.7`,
  }),
};

const sizes = {
  lg: definePartsStyle({
    field: size.lg,
    group: size.lg,
  }),
  md: definePartsStyle({
    field: size.md,
    group: size.md,
  }),
  sm: definePartsStyle({
    field: size.sm,
    group: size.sm,
  }),
  xs: definePartsStyle({
    field: size.xs,
    group: size.xs,
  }),
  base: definePartsStyle({
    field: size.md,
    group: size.md,
  }),
  compact: definePartsStyle({
    field: size.sm,
    group: size.sm,
  }),
};

// variants
const variantOutline = definePartsStyle(() => {
  return {
    field: {
      border: Borders['1PX'],
      borderColor: Colors.BORDER_INPUT,
      bg: Colors.SURFACE_PAGE,
      _hover: {
        borderColor: Colors.BORDER_EMPHASIS,
      },
      _invalid: invalidStyles,
      _focusVisible: focusStyles,
    },
    addon: {
      bg: Colors.SURFACE_INSET,
    },
  };
});

const variantFilled = definePartsStyle(() => {
  return {
    field: {
      border: Borders['1PX'],
      bg: Colors.SURFACE_TERTIARY,
      _hover: {
        borderColor: Colors.TRANSPARENT,
        bg: Colors.SURFACE_TERTIARY_HOVER,
        color: Colors.TEXT_EMPHASIS,
      },
      _active: {
        color: Colors.TEXT_EMPHASIS,
        bg: Colors.SURFACE_PAGE,
      },
      _readOnly: readOnlyStyles,
      _invalid: invalidStyles,
      _focusVisible: focusStyles,
    },
    addon: {
      bg: Colors.SURFACE_TERTIARY,
      border: Borders.NONE,
    },
  };
});

const variantFlushed = definePartsStyle(() => {
  return {
    field: {
      borderBottom: Borders['2PX'],
      borderColor: Colors.BORDER_MUTED,
      borderBottomRadius: '0',
      px: Space['2XS'],
      bg: Colors.TRANSPARENT,
      _hover: {
        bg: Colors.SURFACE_TERTIARY,
        borderColor: Colors.BORDER_EMPHASIS,
      },
      _readOnly: readOnlyStyles,
      _invalid: invalidFlushedStyles,
      _focusVisible: focusFlushedStyles,
    },
    addon: {
      borderBottom: Borders['1PX'],
      borderColor: Borders.TRANSPARENT,
      borderRadius: '0',
      bg: Colors.TRANSPARENT,
      px: Space['2XS'],
    },
  };
});

const variants = {
  outline: variantOutline,
  filled: variantFilled,
  flushed: variantFlushed,
};

export const inputTheme = defineMultiStyleConfig({
  baseStyle,
  sizes,
  variants,
  defaultProps: {
    size: 'md',
    variant: 'outline',
  },
});

export const Input: ComponentStyleConfig = {
  baseStyle,
  variants,
  sizes,
};
